import { LoadingScreen } from '@avanta/shared/components/LoadingScreen';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SurveyProvider, useSurveyContext } from 'contexts/Survey';
import { EndCard } from './components/EndCard';
import { IntroductionCard } from './components/IntroductionCard';
import { QuestionCard } from './components/QuestionCard';
import { QuestionProgress } from './components/QuestionProgress';
import { SurveyCard } from './components/SurveyCard';
import { type SurveyContentProps, type SurveyProps } from './types';

const getMotionKey = (
  isReallyCompleted: boolean,
  questionId?: string,
  surveyId?: string
): string => {
  if (isReallyCompleted) return 'end';
  if (questionId) return `question-${questionId}`;
  if (surveyId) return `survey-${surveyId}`;
  return 'default';
};

const SurveyContent = ({
  title,
  introduction,
  useSurveyName = false,
  allowRepeat = false,
  onClose,
  endCardTitle,
  endCardSubtitle,
  saveButton,
  repeatButton,
  endCardChildren,
  showQuestionProgress = true,
}: SurveyContentProps): React.ReactElement | null => {
  const [showInfo, setShowInfo] = useState(true);
  const { t } = useTranslation(LOCIZE_NS.WELL_BEING);
  const { survey, surveyResponse, question, isLoading, isCompleted } = useSurveyContext();

  if (isLoading) return <LoadingScreen />;

  const isReallyCompleted = Boolean(isCompleted || surveyResponse?.completed);
  if (!survey?.id && !isReallyCompleted && !allowRepeat) return null;
  const hasActiveQuestion = !isReallyCompleted && Boolean(survey && question);
  const displayTitle =
    useSurveyName || !title ? survey?.name || title || '' : t(title) || survey?.name || title;
  const motionKey = getMotionKey(isReallyCompleted, question?.id, survey?.id);
  const subtitle = hasActiveQuestion ? question?.text : '';

  return (
    <div className="flex flex-col gap-4">
      {introduction && showInfo ? (
        <IntroductionCard introduction={introduction} onStartTest={() => setShowInfo(false)} />
      ) : (
        <>
          {hasActiveQuestion && showQuestionProgress && <QuestionProgress />}
          <AnimatePresence mode="wait">
            <motion.div
              key={motionKey}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
            >
              <SurveyCard title={displayTitle} subtitle={subtitle} onClose={onClose}>
                {hasActiveQuestion ? (
                  <QuestionCard saveButton={saveButton ?? `${LOCIZE_NS.COMMON}:Common.next`} />
                ) : (
                  <EndCard
                    title={endCardTitle}
                    subtitle={endCardSubtitle}
                    allowRepeat={allowRepeat}
                    repeatButton={repeatButton}
                  >
                    {endCardChildren}
                  </EndCard>
                )}
              </SurveyCard>
            </motion.div>
          </AnimatePresence>
        </>
      )}
    </div>
  );
};

export const Survey = ({
  surveyType,
  allowRepeat = false,
  onCompleted,
  surveyId,
  ...props
}: SurveyProps): React.ReactElement => (
  <SurveyProvider
    surveyType={surveyType}
    allowRepeat={allowRepeat}
    onCompleted={onCompleted}
    surveyId={surveyId}
  >
    <SurveyContent {...props} allowRepeat={allowRepeat} />
  </SurveyProvider>
);
